/**
 * The Live Wall's Recorded mode: its one fixed shape, and how a live wall hands its cameras over.
 *
 * Recorded mode is always the "1 + 5" shape — a 3×3 grid whose top-left 2×2 (r1c1, r1c2, r2c1,
 * r2c2) is one big window — so six windows, and six is also the cap on cameras played at once.
 * Six on-demand-transcoded HLS streams is what one node can reasonably feed; a 16-tile playback
 * wall would queue most of its tiles behind the playback service's transcode slots.
 *
 * The hand-over travels in the URL (`?cameras=a,b,c&view=…`), not router state, so a reload or a
 * shared link lands on the same cameras, and Recorded → Live returns to the layout it came from.
 */
import { buildWindows, flowCameras, type WallMerge } from '../live-wall/wallLayout';

export const WALL_PLAYBACK_GRID = '3x3';
export const WALL_PLAYBACK_MERGES: WallMerge[] = [{ position: 0, colSpan: 2, rowSpan: 2 }];
export const WALL_PLAYBACK_MAX = 6;

export interface WallPlaybackSlot {
  cameraId: string;
  cameraName: string;
  gridPosition: number;
}

/** The six windows, big first then reading order — the order cameras are poured in. */
export const wallPlaybackWindows = () =>
  [...buildWindows(WALL_PLAYBACK_GRID, WALL_PLAYBACK_MERGES)]
    .sort((a, b) => b.rowSpan * b.colSpan - a.rowSpan * a.colSpan || a.position - b.position);

/**
 * A live wall's cameras in the order they should arrive: its windows largest first, then
 * reading order — so the camera in a live "1 + 7"'s big window lands in the big window here too.
 * Anything past the sixth is left behind, which is the stated rule.
 */
export function liveWallHandOver<T extends { cameraId: string }>(
  gridCameras: (T | null)[],
  gridSize: string,
  merges: WallMerge[],
): string[] {
  const order = [...buildWindows(gridSize, merges)]
    .sort((a, b) => b.rowSpan * b.colSpan - a.rowSpan * a.colSpan || a.position - b.position);
  const ids: string[] = [];
  for (const w of order) {
    const cam = gridCameras[w.position];
    if (cam && !ids.includes(cam.cameraId)) ids.push(cam.cameraId);
    if (ids.length === WALL_PLAYBACK_MAX) break;
  }
  return ids;
}

/** Where the Live | Recorded toggle goes from a live wall. */
export function wallPlaybackHref(playbackPath: string, cameraIds: string[], viewId?: string | null): string {
  const qs = new URLSearchParams();
  if (cameraIds.length) qs.set('cameras', cameraIds.slice(0, WALL_PLAYBACK_MAX).join(','));
  if (viewId) qs.set('view', viewId);
  const s = qs.toString();
  return s ? `${playbackPath}?${s}` : playbackPath;
}

/** Where the toggle goes back to: the live wall, on the layout Recorded mode was opened from. */
export function liveWallHref(livePath: string, viewId?: string | null): string {
  return viewId ? `${livePath}?view=${encodeURIComponent(viewId)}` : livePath;
}

export function parseCameraIds(raw: string | null): string[] {
  const out: string[] = [];
  for (const id of (raw || '').split(',').map((s) => s.trim()).filter(Boolean)) {
    if (!out.includes(id)) out.push(id);
    if (out.length === WALL_PLAYBACK_MAX) break;
  }
  return out;
}

/** Ids → a 9-cell grid with the cameras in the 1 + 5 windows, big first. */
export function slotsFromIds(ids: string[], nameOf: (id: string) => string): (WallPlaybackSlot | null)[] {
  const flat = ids.map((cameraId, i) => ({ cameraId, cameraName: nameOf(cameraId), gridPosition: i }));
  return flowCameras(flat, WALL_PLAYBACK_GRID, WALL_PLAYBACK_MERGES);
}

/** The inverse, for keeping `?cameras=` in step with the wall: big window first. */
export function idsFromSlots(slots: (WallPlaybackSlot | null)[]): string[] {
  return wallPlaybackWindows().map((w) => slots[w.position]?.cameraId).filter(Boolean) as string[];
}
